import {
  WATERMARK_TEXT_FONTS,
  WATERMARK_SIGNATURE_FONTS,
} from "./watermarkFonts";

/** Content modes for watermark text styling. */
export const WATERMARK_CONTENT_TYPES = {
  TEXT: "text",
  SIGNATURE: "signature",
  LOGO: "logo",
};

export { WATERMARK_TEXT_FONTS, WATERMARK_SIGNATURE_FONTS };

/** @deprecated Use WATERMARK_TEXT_FONTS — kept for older imports */
export const WATERMARK_FONTS = WATERMARK_TEXT_FONTS;

export const WATERMARK_SYMBOLS = [
  { id: "copy", label: "©", char: "©" },
  { id: "reg", label: "®", char: "®" },
  { id: "tm", label: "™", char: "™" },
];

/**
 * Text casing presets for watermark / signature text.
 * camel = Capitalize Each Word (spaces kept) — common for brand/watermark copy.
 */
export const WATERMARK_TEXT_CASES = [
  { id: "upper", label: "Upper", title: "Uppercase" },
  { id: "lower", label: "Lower", title: "Lower case" },
  { id: "camel", label: "Camel", title: "Camel case" },
  { id: "sentence", label: "Sentence", title: "Sentence case" },
];

/**
 * Transform watermark text to a casing style.
 * @param {string} text
 * @param {"upper"|"lower"|"camel"|"sentence"} caseId
 */
export function applyWatermarkTextCase(text, caseId) {
  const raw = String(text ?? "");
  switch (caseId) {
    case "upper":
      return raw.toLocaleUpperCase();
    case "lower":
      return raw.toLocaleLowerCase();
    case "camel": {
      // Capitalize each word; keep spaces / hyphens / underscores / slashes.
      return raw
        .toLocaleLowerCase()
        .replace(/(^|[\s\-_\/]+)(\S)/g, (_, sep, ch) => {
          return `${sep}${ch.toLocaleUpperCase()}`;
        });
    }
    case "sentence": {
      const lower = raw.toLocaleLowerCase();
      return lower.replace(/(^\s*\S)|([.!?…]\s*\S)/g, (chunk) =>
        chunk.replace(/\S/, (ch) => ch.toLocaleUpperCase())
      );
    }
    default:
      return raw;
  }
}

export const WATERMARK_EFFECTS = [
  { id: "none", label: "None" },
  { id: "shadow", label: "Shadow" },
  { id: "outline", label: "Outline" },
  { id: "glow", label: "Glow" },
];

/** single | grid | diamond */
export const WATERMARK_TILE_MODES = ["single", "grid", "diamond"];

/**
 * Most-used watermark colors (overlays on photos / product shots).
 * 18 presets — whites, neutrals, and strong accent inks.
 */
export const WATERMARK_COLOR_PRESETS = [
  "#FFFFFF",
  "#F5F5F5",
  "#E0E0E0",
  "#9E9E9E",
  "#666666",
  "#333333",
  "#000000",
  "#B71C1C",
  "#E53935",
  "#FF6F00",
  "#F9A825",
  "#2E7D32",
  "#00897B",
  "#1565C0",
  "#0D47A1",
  "#6A1B9A",
  "#AD1457",
  "#5D4037",
];

/** Defaults applied when switching Tile mode. */
export const WATERMARK_TILE_PRESETS = {
  single: {
    tile: "single",
    size: 4.5,
    opacity: 100,
    color: "#333333",
    rotation: 0,
    spacing: 0,
  },
  grid: {
    tile: "grid",
    size: 1.3,
    opacity: 100,
    color: "#333333",
    rotation: 0,
    spacing: 0.5,
  },
  diamond: {
    tile: "diamond",
    size: 1.3,
    opacity: 100,
    color: "#333333",
    rotation: -25,
    spacing: 0.5,
  },
};

/** 9-point placement anchors (0 = start, 0.5 = mid, 1 = end within inset box). */
export const WATERMARK_POSITIONS = [
  { id: "top-left", label: "Top left", ax: 0, ay: 0 },
  { id: "top", label: "Top", ax: 0.5, ay: 0 },
  { id: "top-right", label: "Top right", ax: 1, ay: 0 },
  { id: "middle-left", label: "Middle left", ax: 0, ay: 0.5 },
  { id: "center", label: "Center", ax: 0.5, ay: 0.5 },
  { id: "middle-right", label: "Middle right", ax: 1, ay: 0.5 },
  { id: "bottom-left", label: "Bottom left", ax: 0, ay: 1 },
  { id: "bottom", label: "Bottom", ax: 0.5, ay: 1 },
  { id: "bottom-right", label: "Bottom right", ax: 1, ay: 1 },
];

export const DEFAULT_WATERMARK_SETTINGS = {
  contentType: WATERMARK_CONTENT_TYPES.TEXT,
  text: "Your Text",
  fontId: "Arial",
  /** normal | bold */
  fontWeight: "normal",
  /** normal | italic */
  fontStyle: "normal",
  underline: false,
  strikethrough: false,
  /** left | center | right | justify — single-line watermark uses justify */
  textAlign: "justify",
  /** Tracking in hundredths of an em */
  letterSpacing: 0,
  lineHeight: 1.4,
  /** top | middle | bottom */
  textVerticalAlign: "top",
  /** normal | super | sub */
  textScript: "normal",
  effect: "none",
  embed: false,
  offsetX: 0,
  offsetY: 0,
  position: "center",
  /** Edge inset as fraction of min(stageW, stageH), 0–0.4 */
  margin: 0.08,
  lockAspectRatio: true,
  /** Object URL or remote URL for logo watermark */
  logoUrl: null,
  logoNaturalWidth: 0,
  logoNaturalHeight: 0,
  /** In-memory File used to call BG removal (not persisted) */
  logoFile: null,
  /** Library ids so BG removal can load bytes via API (avoids S3 CORS) */
  logoCreationId: null,
  logoRootCreationId: null,
  /** Logo URL before BG removal (same as logoUrl until cutout is applied) */
  logoOriginalUrl: null,
  /** Logo image formatting (used by ImageFormatToolbar) */
  logoStrokeColor: "#FFFFFF",
  logoStrokeWidth: 0,
  logoCornerRadius: 0,
  /** @deprecated use logoCropRect */
  logoCropInset: 0,
  logoCropAspect: "custom",
  /** Normalized crop inside logo { x, y, w, h }; null = full image */
  logoCropRect: null,
  /** Host-space crop window while editing (preview only) */
  logoCropFrame: null,
  /** Host-space image placement while editing (preview only) */
  logoCropImageFrame: null,
  /** Interactive crop session on the preview canvas */
  logoCropActive: false,
  /** Snapshot of logoCropRect when crop session starts (Cancel restores this) */
  logoCropSessionStartRect: null,
  /** Snapshot of logoCropImageFrame when crop session starts */
  logoCropSessionStartImageFrame: null,
  logoFlipH: false,
  logoFlipV: false,
  /** Optional color wash for logo marks (null = none) */
  logoTintColor: null,
  /** True after toolbar BG Remover replaced logoUrl with a cutout */
  logoBgRemoved: false,
  /** Last applied job preset id (optional) */
  presetId: null,
  /**
   * Per content-type field snapshots (text / logo / signature).
   * Swapping modes saves the current mode and restores the target.
   */
  byMode: {},
  ...WATERMARK_TILE_PRESETS.single,
};

/** Fields remembered separately for text / logo / signature. */
export const WATERMARK_MODE_STASH_KEYS = [
  "text",
  "fontId",
  "fontWeight",
  "fontStyle",
  "underline",
  "strikethrough",
  "textAlign",
  "letterSpacing",
  "lineHeight",
  "textVerticalAlign",
  "textScript",
  "effect",
  "color",
  "size",
  "opacity",
  "rotation",
  "position",
  "margin",
  "offsetX",
  "offsetY",
  "lockAspectRatio",
  "logoStrokeColor",
  "logoStrokeWidth",
  "logoCornerRadius",
  "logoCropInset",
  "logoCropAspect",
  "logoCropRect",
  "logoCropFrame",
  "logoCropImageFrame",
  "logoCropActive",
  "logoCropSessionStartRect",
  "logoCropSessionStartImageFrame",
  "logoFlipH",
  "logoFlipV",
  "logoTintColor",
  "logoBgRemoved",
];

/** Shared across modes (not overwritten from a mode stash). */
const WATERMARK_MODE_SHARED_KEYS = [
  "tile",
  "spacing",
  "embed",
  "logoUrl",
  "logoNaturalWidth",
  "logoNaturalHeight",
  "flushEdges",
];

function snapshotWatermarkModeFields(settings = {}) {
  const snap = {};
  for (const key of WATERMARK_MODE_STASH_KEYS) {
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      snap[key] = settings[key];
    }
  }
  return snap;
}

/** Defaults used the first time a mode is opened in a session. */
export function defaultWatermarkModeSnapshot(contentType) {
  const snap = snapshotWatermarkModeFields(DEFAULT_WATERMARK_SETTINGS);
  if (contentType === WATERMARK_CONTENT_TYPES.SIGNATURE) {
    const fonts = getWatermarkFontsForType(WATERMARK_CONTENT_TYPES.SIGNATURE);
    snap.fontId = fonts[0]?.id || snap.fontId;
    snap.text = "Your signature";
  } else if (contentType === WATERMARK_CONTENT_TYPES.TEXT) {
    snap.text = DEFAULT_WATERMARK_SETTINGS.text;
    snap.fontId = DEFAULT_WATERMARK_SETTINGS.fontId;
  } else if (contentType === WATERMARK_CONTENT_TYPES.LOGO) {
    snap.effect = "none";
    snap.logoTintColor = null;
    snap.logoBgRemoved = false;
    snap.logoCropActive = false;
    snap.logoCropRect = null;
    snap.logoCropFrame = null;
    snap.logoCropImageFrame = null;
    snap.logoCropSessionStartRect = null;
    snap.logoCropSessionStartImageFrame = null;
  }
  return snap;
}

function pickSharedWatermarkFields(settings = {}) {
  const shared = {};
  for (const key of WATERMARK_MODE_SHARED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      shared[key] = settings[key];
    }
  }
  return shared;
}

export function applyWatermarkTilePreset(settings, tile) {
  const preset = WATERMARK_TILE_PRESETS[tile] || WATERMARK_TILE_PRESETS.single;
  return {
    ...settings,
    ...preset,
    offsetX: 0,
    offsetY: 0,
  };
}

export function applyWatermarkPosition(settings, position) {
  const valid = WATERMARK_POSITIONS.some((p) => p.id === position);
  const nextPosition = valid ? position : "center";
  // Edge / corner presets sit flush (margin 0). Center keeps the default inset.
  const nextMargin = nextPosition === "center" ? 0.08 : 0;
  return {
    ...settings,
    position: nextPosition,
    offsetX: 0,
    offsetY: 0,
    margin: nextMargin,
  };
}

export function getWatermarkFontsForType(contentType) {
  return contentType === WATERMARK_CONTENT_TYPES.SIGNATURE
    ? WATERMARK_SIGNATURE_FONTS
    : WATERMARK_TEXT_FONTS;
}

/**
 * Switch Text / Logo / Signature.
 * Saves the current mode's fields into settings.byMode, then restores the
 * target mode (or mode defaults on first visit). Shared fields (tile, embed,
 * logo asset URL) stay put — no confirm dialog.
 */
export function applyWatermarkContentType(settings, contentType) {
  const prevType = settings?.contentType || WATERMARK_CONTENT_TYPES.TEXT;
  const nextType = [
    WATERMARK_CONTENT_TYPES.TEXT,
    WATERMARK_CONTENT_TYPES.LOGO,
    WATERMARK_CONTENT_TYPES.SIGNATURE,
  ].includes(contentType)
    ? contentType
    : WATERMARK_CONTENT_TYPES.TEXT;

  if (prevType === nextType) {
    return settings;
  }

  const byMode = { ...(settings?.byMode || {}) };
  byMode[prevType] = snapshotWatermarkModeFields(settings);

  const restored =
    byMode[nextType] != null
      ? { ...byMode[nextType] }
      : defaultWatermarkModeSnapshot(nextType);

  if (nextType !== WATERMARK_CONTENT_TYPES.LOGO) {
    restored.logoCropActive = false;
  }

  if (nextType !== WATERMARK_CONTENT_TYPES.LOGO) {
    const fonts = getWatermarkFontsForType(nextType);
    const defaultFont = fonts[0];
    const stillValid = fonts.some((f) => f.id === restored.fontId);
    restored.fontId = stillValid ? restored.fontId : defaultFont?.id || restored.fontId;
  }

  return {
    ...settings,
    ...restored,
    ...pickSharedWatermarkFields(settings),
    contentType: nextType,
    byMode,
    // Preset belonged to the previous mode's look — drop highlight.
    presetId: null,
  };
}

export function isWatermarkLogoMode(settings) {
  return (
    settings?.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
    Boolean(settings?.logoUrl)
  );
}

/**
 * Display size for a logo mark from Size slider (keeps natural / crop aspect).
 */
export function watermarkLogoDisplaySize(
  sizeScale,
  stageWidth,
  stageHeight,
  naturalWidth = 0,
  naturalHeight = 0,
  cropRect = null
) {
  const s = Math.max(0, Math.min(24, Number(sizeScale) || 0));
  const basis = Math.min(
    Math.max(120, Number(stageWidth) || 420),
    Math.max(120, Number(stageHeight) || 420)
  );
  const nw = Number(naturalWidth) || 0;
  const nh = Number(naturalHeight) || 0;
  let aspect = nw > 0 && nh > 0 ? nw / nh : 1;
  if (cropRect && typeof cropRect === "object") {
    const w = Math.max(0.05, Number(cropRect.w) || 1);
    const h = Math.max(0.05, Number(cropRect.h) || 1);
    aspect = (nw > 0 && nh > 0 ? nw / nh : 1) * (w / h);
  }
  const width = Math.max(8, basis * 0.07 * s);
  const height = Math.max(8, width / aspect);
  return { width, height };
}

export function getWatermarkFontStack(fontId) {
  const all = [...WATERMARK_TEXT_FONTS, ...WATERMARK_SIGNATURE_FONTS];
  return (
    all.find((f) => f.id === fontId)?.stack || WATERMARK_TEXT_FONTS[0].stack
  );
}

/** CSS text styles for watermark effect */
export function watermarkEffectStyle(effect, color) {
  switch (effect) {
    case "shadow":
      return { textShadow: "2px 2px 6px rgba(0,0,0,0.55)" };
    case "outline":
      return {
        WebkitTextStroke: `1px ${color === "#FFFFFF" ? "#111" : "#fff"}`,
        paintOrder: "stroke fill",
      };
    case "glow":
      return {
        textShadow: `0 0 8px ${color}, 0 0 16px ${color}`,
      };
    default:
      return { textShadow: "none" };
  }
}

/** CSS filter styles for logo watermark effects */
export function watermarkLogoEffectStyle(effect, color = "#FFFFFF") {
  const stroke =
    String(color).toUpperCase() === "#FFFFFF" ||
    String(color).toUpperCase() === "#FFF"
      ? "#111111"
      : "#ffffff";
  switch (effect) {
    case "shadow":
      return { filter: "drop-shadow(2px 2px 6px rgba(0,0,0,0.55))" };
    case "glow":
      return {
        filter: `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 12px ${color})`,
      };
    case "outline":
      return {
        filter: [
          `drop-shadow(1px 0 0 ${stroke})`,
          `drop-shadow(-1px 0 0 ${stroke})`,
          `drop-shadow(0 1px 0 ${stroke})`,
          `drop-shadow(0 -1px 0 ${stroke})`,
          `drop-shadow(1px 1px 0 ${stroke})`,
          `drop-shadow(-1px -1px 0 ${stroke})`,
        ].join(" "),
      };
    default:
      return {};
  }
}

/**
 * Measure rendered watermark text (browser canvas).
 * Falls back to an estimate when canvas is unavailable.
 */
export function watermarkFontCssWeight(fontWeight) {
  return fontWeight === "bold" ? "bold" : "normal";
}

/** CSS font-style from watermark settings. */
export function watermarkFontCssStyle(fontStyle) {
  return fontStyle === "italic" ? "italic" : "normal";
}

/** CSS text-decoration from underline / strikethrough flags. */
export function watermarkTextDecoration({ underline = false, strikethrough = false } = {}) {
  const parts = [];
  if (underline) parts.push("underline");
  if (strikethrough) parts.push("line-through");
  return parts.length ? parts.join(" ") : "none";
}

/** Split watermark text so A² / A₂ applies to the last letter only. */
export function splitWatermarkScriptText(text) {
  const full = String(text ?? "");
  if (!full.length) return { base: "", affix: "" };
  return {
    base: full.slice(0, -1),
    affix: full.slice(-1),
  };
}

/** Letter / line spacing + page-edge pin (super/sub use last-letter affix). */
export function watermarkTextAdvancedCss(settings = {}, fontSizePx) {
  const tracking = Number(settings.letterSpacing);
  const letterSpacing = Number.isFinite(tracking)
    ? `${tracking / 100}em`
    : "0em";
  const lh = Number(settings.lineHeight);
  const lineHeight = Number.isFinite(lh) && lh > 0 ? lh : 1.4;
  const script = settings.textScript || "normal";
  const vertical = settings.textVerticalAlign || "top";
  void fontSizePx;

  const page =
    WATERMARK_POSITIONS.find((p) => p.id === (settings.position || "center")) ||
    WATERMARK_POSITIONS.find((p) => p.id === "center");
  const pageAx = page?.ax ?? 0.5;
  const pageAy = page?.ay ?? 0.5;

  // Pin glyphs to the page edge being aligned so ink touches the image.
  let alignItems =
    vertical === "bottom"
      ? "flex-end"
      : vertical === "middle"
        ? "center"
        : "flex-start";
  let justifyContent =
    vertical === "bottom"
      ? "flex-end"
      : vertical === "middle"
        ? "center"
        : "flex-start";

  if (pageAy === 0) alignItems = "flex-start";
  if (pageAy === 1) alignItems = "flex-end";
  if (pageAx === 0) justifyContent = "flex-start";
  if (pageAx === 1) justifyContent = "flex-end";
  if (pageAx === 0.5 && pageAy !== 0 && pageAy !== 1) {
    justifyContent = "center";
  }

  // Super/sub still raise/lower the last letter inside the mark-text row.
  if (script === "super" && pageAy !== 1) alignItems = "flex-start";
  if (script === "sub" && pageAy !== 0) alignItems = "flex-end";

  return {
    letterSpacing,
    lineHeight: script === "normal" ? lineHeight : 1,
    display: "inline-flex",
    alignItems,
    justifyContent,
    whiteSpace: "nowrap",
    overflow: "visible",
  };
}

/** Layout box so text-align is visible within the watermark mark. */
export function watermarkTextBoxLayout(
  textAlign,
  metrics = {},
  { boxWidth, extraWidth = 0, textScript = "normal" } = {}
) {
  const align = textAlign || "center";
  const width = Math.max(1, Number(metrics.width) || 1);
  const height = Math.max(1, Number(metrics.height) || 1);
  const script = textScript || "normal";
  const boxSizing = { boxSizing: "border-box" };
  const scriptBox =
    script === "super" || script === "sub"
      ? { height, minHeight: height }
      : {};

  // Always size to measured glyphs so selection / edit clip edge-to-edge.
  const resolvedWidth = boxWidth ?? (extraWidth > 0 ? width + extraWidth : width);
  const justifyContent =
    align === "right"
      ? "flex-end"
      : align === "justify"
        ? "space-between"
        : align === "center"
          ? "center"
          : "flex-start";
  return {
    width: resolvedWidth,
    textAlign: align,
    justifyContent,
    ...boxSizing,
    ...scriptBox,
  };
}

export function measureWatermarkText(
  text,
  fontSizePx,
  fontFamily,
  fontWeight = "normal",
  { letterSpacing = 0, textScript = "normal", fontStyle = "normal" } = {}
) {
  const safeText = String(text || " ").trim() || " ";
  const baseSize = Math.max(1, Number(fontSizePx) || 12);
  const script = textScript || "normal";
  const family = fontFamily || WATERMARK_FONTS[0].stack;
  const weight = watermarkFontCssWeight(fontWeight);
  const style = watermarkFontCssStyle(fontStyle);
  const tracking = Number(letterSpacing);
  const trackingEm = Number.isFinite(tracking) ? tracking / 100 : 0;
  const hasScript = script === "super" || script === "sub";
  const { base, affix } = hasScript
    ? splitWatermarkScriptText(safeText)
    : { base: safeText, affix: "" };
  const scriptSize = hasScript ? baseSize * 0.65 : baseSize;

  const measureChunk = (chunk, sizePx) => {
    if (!chunk) return { width: 0, ascent: 0, descent: 0 };
    let width = sizePx * chunk.length * (weight === "bold" || weight === 700 ? 0.62 : 0.55);
    let ascent = sizePx * 0.8;
    let descent = sizePx * 0.2;
    if (typeof document !== "undefined") {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.font = `${style} ${weight} ${sizePx}px ${family}`;
          const metrics = ctx.measureText(chunk);
          width = Math.max(0, metrics.width);
          ascent = metrics.actualBoundingBoxAscent ?? ascent;
          descent = metrics.actualBoundingBoxDescent ?? descent;
        }
      } catch {
        // keep fallback
      }
    }
    if (trackingEm !== 0 && chunk.length > 1) {
      width += (chunk.length - 1) * trackingEm * sizePx;
    }
    return { width, ascent, descent };
  };

  const baseM = measureChunk(base || (hasScript ? "" : safeText), baseSize);
  const affixM = measureChunk(affix, scriptSize);
  let width = Math.max(1, baseM.width + affixM.width);
  if (trackingEm !== 0 && base && affix) {
    width += trackingEm * baseSize;
  }

  const baseH = Math.max(
    1,
    (baseM.ascent || baseSize * 0.8) + (baseM.descent || baseSize * 0.2)
  );
  // Prefer ink box; cap so selection height doesn’t leave empty top/bottom padding.
  let height = Math.min(baseH, baseSize * 1.05);
  height = Math.max(1, height);
  if (hasScript) {
    // Tight box: base glyph height + raise/lower of last letter (no empty pad).
    const raise = Math.round(baseSize * 0.3);
    height = Math.max(1, height + raise);
  }

  return { width, height };
}

/**
 * Map Size (0–24x) → font pixels.
 * Tuned so ~1.3x matches competitor visual weight (previous 0.033 was ~60% too large).
 */
export function watermarkFontSizePx(sizeScale, stageWidth, stageHeight) {
  const s = Math.max(0, Math.min(24, Number(sizeScale) || 0));
  const w = Math.max(120, Number(stageWidth) || 420);
  const h = Math.max(120, Number(stageHeight) || w);
  // Use the shorter side so wide previews don’t inflate glyphs.
  const basis = Math.min(w, h);
  return Math.max(1, Math.round(basis * 0.024 * s));
}

/**
 * Axis-aligned half-extents of a centered mark after rotation.
 * Needed so margin/edge placement hugs the visual bounds (e.g. -90° text).
 */
export function rotatedMarkHalfExtents(
  textWidth,
  textHeight,
  rotationDeg = 0
) {
  const w = Math.max(1, Number(textWidth) || 1);
  const h = Math.max(1, Number(textHeight) || 1);
  const rad = ((Number(rotationDeg) || 0) * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  return {
    halfW: (w * c + h * s) / 2,
    halfH: (w * s + h * c) / 2,
  };
}

const NEAREST_EDGE_ORDER = ["left", "right", "top", "bottom"];

/**
 * Pixel gaps from the rotated AABB to each stage edge.
 * centerX / centerY are normalized (0–1) mark centers.
 */
export function markEdgeGapsPx({
  centerX = 0.5,
  centerY = 0.5,
  stageWidth = 1,
  stageHeight = 1,
  textWidth = 1,
  textHeight = 1,
  rotationDeg = 0,
}) {
  const sw = Math.max(1, Number(stageWidth) || 1);
  const sh = Math.max(1, Number(stageHeight) || 1);
  const { halfW, halfH } = rotatedMarkHalfExtents(
    textWidth,
    textHeight,
    rotationDeg
  );
  const cx = Number(centerX) * sw;
  const cy = Number(centerY) * sh;
  return {
    left: cx - halfW,
    right: sw - (cx + halfW),
    top: cy - halfH,
    bottom: sh - (cy + halfH),
  };
}

/** Pick the nearest edge (stable tie-break: left → right → top → bottom). */
export function resolveNearestEdge(gaps = {}) {
  let edge = "left";
  let gapPx = Number(gaps.left);
  if (!Number.isFinite(gapPx)) gapPx = 0;
  for (const id of NEAREST_EDGE_ORDER) {
    const g = Number(gaps[id]);
    if (!Number.isFinite(g)) continue;
    if (g < gapPx - 1e-6) {
      edge = id;
      gapPx = g;
    }
  }
  return { edge, gapPx };
}

/**
 * Corner presets (top-left / top-right / bottom-left / bottom-right)
 * return the two edges that should share the same margin inset.
 */
export function resolveCornerEdges(position = "center") {
  const anchor =
    WATERMARK_POSITIONS.find((p) => p.id === position) ||
    WATERMARK_POSITIONS.find((p) => p.id === "center");
  const ax = anchor?.ax ?? 0.5;
  const ay = anchor?.ay ?? 0.5;
  const isCorner = (ax === 0 || ax === 1) && (ay === 0 || ay === 1);
  if (!isCorner) return null;
  return {
    xEdge: ax === 0 ? "left" : "right",
    yEdge: ay === 0 ? "top" : "bottom",
  };
}

/**
 * Pixel gap used for margin % display / sync.
 * Corners → min of the two corner edges; otherwise → global nearest edge.
 */
export function resolveMarginGapPx(gaps = {}, position = "center") {
  const corner = resolveCornerEdges(position);
  if (corner) {
    const gx = Number(gaps[corner.xEdge]);
    const gy = Number(gaps[corner.yEdge]);
    return Math.min(
      Number.isFinite(gx) ? gx : 0,
      Number.isFinite(gy) ? gy : 0
    );
  }
  return resolveNearestEdge(gaps).gapPx;
}

/** Place mark center so the given edge gap equals targetPx (px). */
function applyEdgeGapToCenter(edge, targetPx, cx, cy, sw, sh, halfW, halfH) {
  let nextX = cx;
  let nextY = cy;
  if (edge === "left") nextX = halfW + targetPx;
  else if (edge === "right") nextX = sw - halfW - targetPx;
  else if (edge === "top") nextY = halfH + targetPx;
  else if (edge === "bottom") nextY = sh - halfH - targetPx;
  return { cx: nextX, cy: nextY };
}

/** Convert a pixel edge gap → margin fraction (of min stage side), clamped. */
export function marginFromNearestEdgeGap(
  gapPx,
  stageWidth,
  stageHeight,
  maxMargin = 0.4
) {
  const sw = Math.max(1, Number(stageWidth) || 1);
  const sh = Math.max(1, Number(stageHeight) || 1);
  const base = Math.min(sw, sh);
  const m = Math.max(0, Number(gapPx) || 0) / base;
  return Math.max(0, Math.min(maxMargin, m));
}

/**
 * Derive margin % from the nearest edge at the given visual center,
 * and rebase offsetX/Y so the mark stays put when margin is stored.
 * Corner presets use the shared (min) gap of the two corner edges.
 */
export function syncMarginFromNearestEdge({
  centerX,
  centerY,
  position = "center",
  stageWidth,
  stageHeight,
  textWidth,
  textHeight,
  rotationDeg = 0,
  flushEdges = true,
  maxMargin = 0.4,
}) {
  const gaps = markEdgeGapsPx({
    centerX,
    centerY,
    stageWidth,
    stageHeight,
    textWidth,
    textHeight,
    rotationDeg,
  });
  const corner = resolveCornerEdges(position);
  let edge;
  let gapPx;
  if (corner) {
    gapPx = resolveMarginGapPx(gaps, position);
    edge = `${corner.yEdge}-${corner.xEdge}`;
  } else {
    ({ edge, gapPx } = resolveNearestEdge(gaps));
  }
  const margin = marginFromNearestEdgeGap(
    gapPx,
    stageWidth,
    stageHeight,
    maxMargin
  );
  const base = resolveSingleMarkPosition({
    position,
    margin,
    stageWidth,
    stageHeight,
    textWidth,
    textHeight,
    rotationDeg,
    flushEdges,
  });
  return {
    margin,
    nearestEdge: edge,
    offsetX: Number(centerX) - base.x,
    offsetY: Number(centerY) - base.y,
  };
}

/**
 * Move the mark so the nearest-edge gap matches nextMargin.
 * Corner presets inset evenly on both axes (same gap on X and Y edges).
 * Mid-edge presets move along a single axis only.
 */
export function applyMarginAlongNearestEdge({
  centerX,
  centerY,
  position = "center",
  nextMargin = 0,
  stageWidth,
  stageHeight,
  textWidth,
  textHeight,
  rotationDeg = 0,
  flushEdges = true,
  maxMargin = 0.4,
}) {
  const sw = Math.max(1, Number(stageWidth) || 1);
  const sh = Math.max(1, Number(stageHeight) || 1);
  const m = Math.max(0, Math.min(maxMargin, Number(nextMargin) || 0));
  const gaps = markEdgeGapsPx({
    centerX,
    centerY,
    stageWidth: sw,
    stageHeight: sh,
    textWidth,
    textHeight,
    rotationDeg,
  });
  const { halfW, halfH } = rotatedMarkHalfExtents(
    textWidth,
    textHeight,
    rotationDeg
  );
  const targetPx = m * Math.min(sw, sh);

  let cx = Number(centerX) * sw;
  let cy = Number(centerY) * sh;
  const corner = resolveCornerEdges(position);
  let nearestEdge;

  if (corner) {
    ({ cx, cy } = applyEdgeGapToCenter(
      corner.xEdge,
      targetPx,
      cx,
      cy,
      sw,
      sh,
      halfW,
      halfH
    ));
    ({ cx, cy } = applyEdgeGapToCenter(
      corner.yEdge,
      targetPx,
      cx,
      cy,
      sw,
      sh,
      halfW,
      halfH
    ));
    nearestEdge = `${corner.yEdge}-${corner.xEdge}`;
  } else {
    const { edge } = resolveNearestEdge(gaps);
    nearestEdge = edge;
    ({ cx, cy } = applyEdgeGapToCenter(
      edge,
      targetPx,
      cx,
      cy,
      sw,
      sh,
      halfW,
      halfH
    ));
  }

  const nx = cx / sw;
  const ny = cy / sh;
  const base = resolveSingleMarkPosition({
    position,
    margin: m,
    stageWidth: sw,
    stageHeight: sh,
    textWidth,
    textHeight,
    rotationDeg,
    flushEdges,
  });
  return {
    margin: m,
    nearestEdge,
    offsetX: nx - base.x,
    offsetY: ny - base.y,
  };
}

/**
 * Place a single mark using position preset.
 * Returns normalized center coords (0–1).
 *
 * When flushEdges is true (default, watermark maker): Left / Right / Top /
 * Bottom pin the mark flush to the image edge (no margin on that axis).
 * Margin only applies toward the free center axis.
 *
 * When flushEdges is false (page numbers): margin insets from every edge,
 * including the pinned axis — so Margin actually moves bottom/top marks.
 */
export function resolveSingleMarkPosition({
  position = "center",
  margin = 0.08,
  stageWidth = 420,
  stageHeight = 560,
  textWidth = 80,
  textHeight = 20,
  rotationDeg = 0,
  flushEdges = true,
}) {
  const sw = Math.max(1, Number(stageWidth) || 1);
  const sh = Math.max(1, Number(stageHeight) || 1);
  const tw = Math.max(1, Number(textWidth) || 1);
  const th = Math.max(1, Number(textHeight) || 1);
  const { halfW, halfH } = rotatedMarkHalfExtents(tw, th, rotationDeg);

  const anchor =
    WATERMARK_POSITIONS.find((p) => p.id === position) ||
    WATERMARK_POSITIONS.find((p) => p.id === "center");
  const ax = anchor?.ax ?? 0.5;
  const ay = anchor?.ay ?? 0.5;

  const m = Math.max(0, Math.min(0.4, Number(margin) || 0));
  const marginPx = Math.min(sw, sh) * m;
  const padX =
    flushEdges && (ax === 0 || ax === 1) ? 0 : marginPx;
  const padY =
    flushEdges && (ay === 0 || ay === 1) ? 0 : marginPx;

  let minX = (padX + halfW) / sw;
  let maxX = 1 - (padX + halfW) / sw;
  let minY = (padY + halfH) / sh;
  let maxY = 1 - (padY + halfH) / sh;

  if (minX > maxX) {
    minX = 0.5;
    maxX = 0.5;
  }
  if (minY > maxY) {
    minY = 0.5;
    maxY = 0.5;
  }

  let x = minX + ax * (maxX - minX);
  let y = minY + ay * (maxY - minY);
  if (flushEdges) {
    // Exact flush for hard edges (avoids float drift).
    if (ax === 0) x = halfW / sw;
    if (ax === 1) x = 1 - halfW / sw;
    if (ay === 0) y = halfH / sh;
    if (ay === 1) y = 1 - halfH / sh;
  }

  return { x, y };
}

/**
 * Apply rotation while keeping the mark's visual center fixed.
 * Edge presets recompute AABB with rotation; without offset compensation the
 * mark drifts (especially wide logos at top/bottom/corners).
 */
export function watermarkRotationPreservingCenter({
  nextRotation,
  centerX,
  centerY,
  position = "center",
  margin = 0.08,
  stageWidth,
  stageHeight,
  textWidth,
  textHeight,
  flushEdges = true,
}) {
  const rotation = Math.max(-180, Math.min(180, Number(nextRotation) || 0));
  const base = resolveSingleMarkPosition({
    position,
    margin,
    stageWidth,
    stageHeight,
    textWidth,
    textHeight,
    rotationDeg: rotation,
    flushEdges,
  });
  return {
    rotation,
    offsetX: Number(centerX) - base.x,
    offsetY: Number(centerY) - base.y,
  };
}

/**
 * Pixel-based tile positions (normalized 0–1).
 * Spacing adds gap between marks; margin insets the fill area from stage edges.
 * Margin 0 places first marks flush to the top/left (logo edges on the stage).
 * offsetX/offsetY pan the pattern; extra tiles are generated so edges stay filled.
 * Single tile uses position + margin via resolveSingleMarkPosition.
 */
export function watermarkTilePositions({
  tile = "single",
  spacing = 0,
  stageWidth = 420,
  stageHeight = 560,
  textWidth = 80,
  textHeight = 20,
  fontSizePx = 16,
  rotationDeg = 0,
  position = "center",
  margin = 0.08,
  offsetX = 0,
  offsetY = 0,
  flushEdges = true,
}) {
  if (tile === "single") {
    const pos = resolveSingleMarkPosition({
      position,
      margin,
      stageWidth,
      stageHeight,
      textWidth,
      textHeight,
      rotationDeg,
      flushEdges,
    });
    return [
      {
        x: pos.x + (Number(offsetX) || 0),
        y: pos.y + (Number(offsetY) || 0),
      },
    ];
  }

  const s = Math.max(0, Math.min(3.96, Number(spacing) || 0));
  const basePad = Math.max(6, (Number(fontSizePx) || 16) * 1.25);
  const extra = Math.max(textWidth, textHeight) * s;

  let stepX = Math.max(2, textWidth + basePad + extra);
  let stepY = Math.max(2, textHeight + basePad + extra);

  const staggered = tile === "diamond";
  const m = Math.max(0, Math.min(0.4, Number(margin) || 0));
  const marginPx = Math.min(stageWidth, stageHeight) * m;
  const rot = Math.abs(Number(rotationDeg) || 0);
  const rotPad =
    rot > 5 ? Math.max(stepX, stepY) * 0.35 : 0;

  const x0 = marginPx - rotPad;
  const y0 = marginPx - rotPad;
  const x1 = stageWidth - marginPx + rotPad;
  const y1 = stageHeight - marginPx + rotPad;

  const halfW = Math.max(1, textWidth * 0.5);
  const halfH = Math.max(1, textHeight * 0.5);

  const offPxX = (Number(offsetX) || 0) * stageWidth;
  const offPxY = (Number(offsetY) || 0) * stageHeight;

  // Fixed grid anchor (offset 0 = flush top/left at margin). Pan adds offPx to every mark.
  const anchorX = x0 + halfW;
  const anchorY = y0 + halfH;

  const MAX_MARKS = 1600;

  // Extend column/row indices so panned pattern still covers the inset area.
  const jMin = Math.floor((y0 + halfH - anchorY - offPxY) / stepY);
  const jMax = Math.ceil((y1 - halfH - anchorY - offPxY) / stepY);
  const baseCols = Math.max(1, Math.ceil((x1 - x0 - halfW * 2) / stepX) + 1);
  const baseRows = Math.max(1, jMax - jMin + 1);
  const estimated = baseCols * baseRows;
  if (estimated > MAX_MARKS) {
    const factor = Math.sqrt(estimated / MAX_MARKS);
    stepX *= factor;
    stepY *= factor;
  }

  const positions = [];
  for (let j = jMin; j <= jMax; j += 1) {
    const staggerRow = ((j % 2) + 2) % 2 === 1;
    const xOffset = staggered && staggerRow ? stepX * 0.5 : 0;
    const iMin = Math.floor((x0 + halfW - anchorX - xOffset - offPxX) / stepX);
    const iMax = Math.ceil((x1 - halfW - anchorX - xOffset - offPxX) / stepX);
    for (let i = iMin; i <= iMax; i += 1) {
      const x = anchorX + i * stepX + xOffset + offPxX;
      const y = anchorY + j * stepY + offPxY;
      positions.push({
        x: x / stageWidth,
        y: y / stageHeight,
      });
      if (positions.length >= MAX_MARKS) {
        return positions;
      }
    }
  }

  if (!positions.length) {
    const fallback = resolveSingleMarkPosition({
      position,
      margin,
      stageWidth,
      stageHeight,
      textWidth,
      textHeight,
      rotationDeg,
      flushEdges,
    });
    return [
      {
        x: fallback.x + (Number(offsetX) || 0),
        y: fallback.y + (Number(offsetY) || 0),
      },
    ];
  }

  return positions;
}
